import { ShippingAccountTypeEnum } from 'domains/jobconfigDomain';
import { RFMOffer, RFMOfferUI } from 'domains/workspaceDomain';
import moment from 'moment';

export type OfferShippingError = 'methodRequired' | 'shipDateInPast' | null;

/**
 * Same rules the RFQ Determine Shipping step enforces: a shipment needs a method, and a ship date
 * can never be in the past. Offers with no shipping selections leave the job's shipping untouched.
 */
export const validateOfferShipping = (
  offer?: RFMOfferUI
): OfferShippingError => {
  const adjustments = offer?.shippingAdjustments;
  if (!adjustments?.length) {
    return null;
  }
  if (adjustments.some((adjustment) => !adjustment.carrierService)) {
    return 'methodRequired';
  }
  const today = moment().startOf('day');
  if (
    adjustments.some(
      (adjustment) =>
        !adjustment.estimatedShipTime ||
        moment(adjustment.estimatedShipTime).isBefore(today, 'day')
    )
  ) {
    return 'shipDateInPast';
  }
  return null;
};

/**
 * Ship date an offer ships against: the earliest one across its own shipments, falling back to the
 * job's, since a round only carries shipping of its own once a sourcer picks it.
 */
export const offerShipDate = (offer: RFMOffer): string | undefined =>
  (offer.shippingAdjustments || [])
    .map((adjustment) => adjustment.estimatedShipTime)
    .filter((shipTime) => !!shipTime)
    .sort()[0] || offer.jobShipDate || undefined;

/**
 * Shipping method of an offer, joined when its shipments do not share one, falling back to the
 * job's own method.
 */
export const offerShippingMethod = (offer: RFMOffer): string | undefined => {
  const methods = Array.from(
    new Set(
      (offer.shippingAdjustments || [])
        .map((adjustment) => adjustment.carrierService)
        .filter((carrierService) => !!carrierService)
    )
  );
  return methods.length
    ? methods.join(', ')
    : offer.jobShippingMethod || undefined;
};

/**
 * Freight of an offer whoever is paying for it. The supplier is only paid freight when the shipping
 * goes on its own account, so a zero there does not mean the job ships for free.
 */
export const offerFreight = (offer: RFMOffer): number =>
  offer.shippingPrice || offer.revshopShippingPrice || 0;

export const offerShippingAccountType = (
  offer: RFMOffer
): ShippingAccountTypeEnum | undefined =>
  (offer.shippingAdjustments || [])
    .map((adjustment) => adjustment.accountType)
    .find((accountType) => !!accountType) ||
  offer.jobShippingAccountType ||
  undefined;

/**
 * Named by role for the supplier and by company otherwise, the way RFQ Determine Shipping does it.
 */
export const offerShippingAccountLabel = (offer: RFMOffer): string => {
  const accountType = offerShippingAccountType(offer);
  if (!accountType) {
    return '-';
  }
  return accountType === ShippingAccountTypeEnum.SUPPLIER
    ? 'Supplier'
    : offer.jobShippingAccount || accountType;
};
